import { X, Sparkles, Bug, RefreshCw, Wrench } from 'lucide-react';

export interface ChangelogEntry {
  version: string;
  date: string;
  changes: {
    type: 'feat' | 'fix' | 'refactor' | 'chore' | 'perf';
    description: string;
  }[];
}

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  changelog: ChangelogEntry[];
  currentVersion: string;
}

const typeConfig = {
  feat: { icon: Sparkles, label: 'Tính năng mới', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  fix: { icon: Bug, label: 'Sửa lỗi', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  refactor: { icon: RefreshCw, label: 'Cải tiến', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  chore: { icon: Wrench, label: 'Bảo trì', color: 'text-slate-400', bg: 'bg-slate-500/10' },
  perf: { icon: RefreshCw, label: 'Hiệu suất', color: 'text-violet-400', bg: 'bg-violet-500/10' },
};

export default function ChangelogModal({ isOpen, onClose, changelog, currentVersion }: ChangelogModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-[60]"
      onClick={onClose}
    >
      <div
        className="bg-[#1a1f2e] rounded-2xl border border-white/10 shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold text-white">Lịch sử cập nhật</h2>
            <p className="text-xs text-white/50 font-mono mt-0.5">Phiên bản hiện tại: {currentVersion}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Changelog entries */}
        <div className="overflow-y-auto p-5 space-y-6 flex-1">
          {changelog.map((entry, idx) => (
            <div key={entry.version}>
              {/* Version header */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-white font-mono">{entry.version}</span>
                <span className="text-xs text-white/40">{entry.date}</span>
                {idx === 0 && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
                    Mới nhất
                  </span>
                )}
              </div>

              {/* Changes list */}
              <div className="space-y-2 ml-1">
                {entry.changes.map((change, cIdx) => {
                  const config = typeConfig[change.type];
                  const Icon = config.icon;
                  return (
                    <div key={cIdx} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center ${config.bg} mt-0.5`}>
                        <Icon className={`w-3 h-3 ${config.color}`} />
                      </div>
                      <span className="text-sm text-white/80 leading-relaxed">{change.description}</span>
                    </div>
                  );
                })}
              </div>

              {/* Divider */}
              {idx < changelog.length - 1 && (
                <div className="border-b border-white/5 mt-5" />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
